// PostContext.tsx
import { createContext, useContext, useState, useCallback } from "react";
import { supabase } from "../supabase/supabaseClient";
import { Post } from "../types/board";
import { useUser } from "../contexts/UserContext";

export interface Like {
  id: number;
  target_type: string; // "post" | "comment" 등
  target_id: number;
  user_id: string;
  vote: number; // 1(좋아요) | -1(비추천) 등
}

interface PostContextType {
  posts: Post[];
  loadPosts: (boardType: string) => Promise<void>;
  addPost: (post: Post) => Promise<void>;
  likePost: (id: number) => void;
  removePost: (id: number) => Promise<void>;
  updatePost: (post: Post) => Promise<void>;
  likes: Like[];
  myReactions: Record<string, number>;
  handleReaction: (targetType: string, targetId: number, vote: number) => Promise<void>;
  loadLikes: (targetType: string, targetIds: number[]) => Promise<void>;
  lastBoardType: string;
}

const PostContext = createContext<PostContextType | null>(null);

export function PostProvider({ children }: { children: React.ReactNode }) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [lastBoardType, setLastBoardType] = useState<string>("");
  const [likes, setLikes] = useState<Like[]>([]);
  const [myReactions, setMyReactions] = useState<Record<string, number>>({});
  const { user } = useUser();

  // 게시글 목록 로딩
  async function loadPosts(boardType: string) {
    setLastBoardType(boardType);
    const { data } = await supabase
      .from("posts")
      .select("*, comments(count)")
      .eq("board_type", boardType)
      .order("id", { ascending: false });

    if (data) setPosts(data as Post[]);

    // 게시글 좋아요(반응) 상태 로딩
    if (data && data.length > 0) {
      const ids = (data as any[]).map((p) => p.id);
      await loadLikes("post", ids);
    }
  }

  // 게시글 추가
  async function addPost(post: Post) {
    const { error } = await supabase.from("posts").insert([post]);
    if (!error) await loadPosts(post.board_type);
  }

  // (구) posts 테이블의 likes 숫자 증가용 - 기존 유지
  async function likePost(id: number) {
    const post = posts.find((p) => p.id === id);
    if (!post) return;
    const newLikes = (post.likes || 0) + 1;
    const { error } = await supabase
      .from("posts")
      .update({ likes: newLikes })
      .eq("id", id);
    if (error) {
      alert("추천 실패: " + error.message);
      return;
    }
    await loadPosts(post.board_type);
  }

  // 게시글 삭제
  async function removePost(id: number) {
    await supabase.from("posts").delete().eq("id", id);
    setPosts((posts) => posts.filter((p) => p.id !== id));
  }

  // 게시글 수정
  async function updatePost(post: Post) {
    const { error } = await supabase
      .from("posts")
      .update({
        title: post.title,
        content: post.content,
      })
      .eq("id", post.id);

    if (!error) {
      setPosts((posts) =>
        posts.map((p) =>
          p.id === post.id ? { ...p, title: post.title, content: post.content } : p
        )
      );
    }
  }

  /**
   * 좋아요/비추천 데이터 로딩 (부분 병합 방식)
   * - targetIds가 비어있으면 해당 targetType 전체 삭제(기존 동작 유지)
   * - targetIds가 있으면 해당 id들의 레코드만 교체(merge)
   */
  const loadLikes = useCallback(
    async (targetType: string, targetIds: number[]) => {
      if (!targetIds || targetIds.length === 0) {
        // 기존 동작 유지: 해당 타입 전체 제거
        setLikes((l) => l.filter((lk) => lk.target_type !== targetType));
        return;
      }

      const { data } = await supabase
        .from("likes")
        .select("*")
        .in("target_id", targetIds)
        .eq("target_type", targetType);

      // 부분 교체(merge): 같은 타입 & 같은 targetIds만 교체하고 나머지는 유지
      setLikes((prev) => {
        const keep = prev.filter(
          (lk) => !(lk.target_type === targetType && targetIds.includes(lk.target_id))
        );
        return [...keep, ...(data || [])];
      });

      // 내 반응 테이블도 부분 병합
      if (user) {
        setMyReactions((prev) => {
          const reactions = { ...prev };
          (data || []).forEach((like: Like) => {
            if (like.user_id === user.id) {
              reactions[`${like.target_type}|${like.target_id}`] = like.vote;
            }
          });
          return reactions;
        });
      }
    },
    [user]
  );

  /**
   * 좋아요/비추천 토글
   * - UI를 낙관적으로 먼저 반영
   * - 서버 결과 성공 시 loadLikes(targetType, [targetId]) 호출
   * - 실패 시 롤백
   */
  const handleReaction = useCallback(
    async (targetType: string, targetId: number, vote: number) => {
      if (!user || targetId === undefined) return;

      const key = `${targetType}|${targetId}`;

      // 1) 낙관적 업데이트 (내 반응만 먼저 갱신)
      setMyReactions((prev) => {
        if (prev[key] === vote) return { ...prev, [key]: 0 }; // 같은 값 누르면 취소
        return { ...prev, [key]: vote };
      });

      try {
        // 2) 서버 반영
        const { data: existing } = await supabase
          .from("likes")
          .select("id, vote")
          .eq("target_type", targetType)
          .eq("target_id", targetId)
          .eq("user_id", user.id);

        if (existing && existing.length > 0) {
          if (existing[0].vote === vote) {
            // 같은 값 → 취소(삭제)
            await supabase.from("likes").delete().eq("id", existing[0].id);
          } else {
            // 다른 값 → 업데이트
            await supabase.from("likes").update({ vote }).eq("id", existing[0].id);
          }
        } else {
          // 신규 삽입
          await supabase
            .from("likes")
            .insert({ target_type: targetType, target_id: targetId, user_id: user.id, vote });
        }

        // 3) 부분 최신화: 해당 타깃만 다시 로드(merge)
        await loadLikes(targetType, [targetId]);
      } catch (err) {
        // 서버 실패 시 롤백
        setMyReactions((prev) => ({ ...prev, [key]: 0 }));
        alert("추천 처리 실패!");
      }
    },
    [user, loadLikes]
  );

  return (
    <PostContext.Provider
      value={{
        posts,
        loadPosts,
        addPost,
        likePost,
        removePost,
        updatePost,
        likes,
        myReactions,
        handleReaction,
        loadLikes,
        lastBoardType,
      }}
    >
      {children}
    </PostContext.Provider>
  );
}

export function usePosts() {
  const ctx = useContext(PostContext);
  if (!ctx) throw new Error("PostContext missing");
  return ctx;
}
